
import React, { useState, useEffect, useRef } from 'react';
import Logo, { CleroS, CleroWordmarkSvg } from './Logo';
import { saveLeadToCloud } from '../services/cloudService';
import PhoneInput from './PhoneInput';

interface Props {
  onStartDemo: () => void;
}

const PilotPage: React.FC<Props> = ({ onStartDemo }) => {
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [formState, setFormState] = useState<'idle' | 'submitting' | 'success'>('idle');
  const [scrolled, setScrolled] = useState(false);
  const [phone, setPhone] = useState('');
  const [contactName, setContactName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [submissionId, setSubmissionId] = useState<string | null>(null);
  const activeSubmissionRef = useRef<string | null>(null);

  const updateContactName = (val: string) => {
    setContactName(val);
    setSubmissionId(null);
  };
  const updateContactEmail = (val: string) => {
    setContactEmail(val);
    setSubmissionId(null);
  };
  const updatePhone = (val: string) => {
    setPhone(val);
    setSubmissionId(null);
  };

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleContactSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (formState === 'submitting') return;
    setFormState('submitting');

    const currentSubmissionId = submissionId || `lead_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
    setSubmissionId(currentSubmissionId);
    activeSubmissionRef.current = currentSubmissionId;

    const leadData: Record<string, any> = {
      name: contactName,
      email: contactEmail,
      submissionId: currentSubmissionId,
    };
    if (phone && phone.trim()) {
      leadData.phone = phone.trim();
    }

    try {
      await saveLeadToCloud(leadData);
      // Only commit success state if this request is still the active submission
      if (activeSubmissionRef.current === currentSubmissionId) {
        setFormState('success');
        setContactName('');
        setContactEmail('');
        setPhone('');
        setSubmissionId(null);
        activeSubmissionRef.current = null;
      }
    } catch (err) {
      console.error("Cloud submission failed", err);
      if (activeSubmissionRef.current === currentSubmissionId) {
        setFormState('idle');
        alert("Something went wrong. Please try again later.");
      }
    }
  };

  return (
    <div className="min-h-screen bg-white text-black font-sans selection:bg-teal-100 selection:text-teal-900 overflow-x-hidden">
      {/* Header */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${scrolled ? 'bg-white/70 backdrop-blur-xl border-b border-gray-100 py-4' : 'bg-transparent py-8'}`}>
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
          <button onClick={() => window.history.pushState({}, '', '/')}>
            <CleroWordmarkSvg height={24} color="#0D9488" />
          </button>

          <div className="hidden md:flex items-center space-x-10">
            <button
              onClick={() => window.history.pushState({}, '', '/')}
              className="text-[12px] font-bold uppercase tracking-widest text-gray-500 hover:text-black transition-colors"
            >
              Consumer
            </button>
            <button
              onClick={onStartDemo}
              className="bg-black text-white px-6 py-2.5 rounded-full text-[12px] font-bold uppercase tracking-widest hover:bg-gray-800 transition-all active:scale-95"
            >
              Try Demo
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-40 pb-20 px-6 overflow-hidden">
        <div className="max-w-7xl mx-auto text-center space-y-10 relative z-10">
          <div className="inline-flex items-center space-y-4 flex-col">
            <div className="inline-flex items-center space-x-2 px-4 py-1.5 bg-teal-50 rounded-full border border-teal-100 animate-fadeIn">
              <span className="flex h-2 w-2 rounded-full bg-teal-500"></span>
              <span className="text-[10px] font-bold text-teal-700 uppercase tracking-widest">Partner Program 2024</span>
            </div>
            <h1 className="text-5xl md:text-8xl font-black tracking-tight leading-[0.95] animate-slideUp">
              Better checkouts. <br />
              <span className="text-teal-600 italic text-4xl md:text-7xl">More group revenue.</span>
            </h1>
          </div>

          <p className="text-xl md:text-2xl text-gray-500 max-w-2xl mx-auto leading-relaxed font-medium animate-slideUp" style={{ animationDelay: '0.1s' }}>
            Clero integrates seamlessly into your checkout flow, allowing customers to handle group payments after purchasing—without changing how you get paid.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-4 animate-slideUp" style={{ animationDelay: '0.2s' }}>
            <button
              onClick={() => setIsContactModalOpen(true)}
              className="w-full sm:w-auto bg-black text-white px-10 py-5 rounded-[2rem] font-bold text-lg hover:bg-gray-800 transition-all flex items-center justify-center space-x-3 shadow-2xl shadow-black/10 group active:scale-95"
            >
              <span>Request Partner Access</span>
              <i className="fas fa-paper-plane text-xs group-hover:translate-x-1 transition-transform"></i>
            </button>
            <button
              onClick={onStartDemo}
              className="w-full sm:w-auto bg-white text-black border-2 border-gray-100 px-10 py-5 rounded-[2rem] font-bold text-lg hover:border-gray-300 transition-all flex items-center justify-center space-x-3 active:scale-95"
            >
              <span>Try Demo</span>
              <i className="fas fa-arrow-right text-gray-400"></i>
            </button>
          </div>
        </div>

        {/* Decorative elements */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[120%] h-[120%] bg-teal-50/30 rounded-full blur-[120px] -z-10"></div>
      </section>

      {/* Features Grid */}
      <section className="py-32 bg-gray-50/50 px-6">
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-12">
          <div className="bg-white p-12 rounded-[3.5rem] border border-gray-100 space-y-8 h-full">
            <h2 className="text-[12px] font-bold text-teal-600 uppercase tracking-[0.4em]">What Clero Does</h2>
            <ul className="space-y-6">
              {[
                "Customers buy and pay you in full, as usual",
                "During checkout, the organizer can turn on Clero",
                "Clero automatically sends payment links and tracks status"
              ].map((text, i) => (
                <li key={i} className="flex items-start space-x-4 text-lg font-bold text-gray-700">
                  <i className="fas fa-check text-teal-500 mt-1"></i>
                  <span>{text}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="bg-white p-12 rounded-[3.5rem] border border-gray-100 space-y-8 h-full">
            <h2 className="text-[12px] font-bold text-teal-600 uppercase tracking-[0.4em]">What You Get</h2>
            <ul className="space-y-6">
              {[
                "Faster group purchase decisions",
                "Streamlined checkout experience",
                "Higher repeat customer rates",
                "Zero risk to existing revenue"
              ].map((text, i) => (
                <li key={i} className="flex items-start space-x-4 text-lg font-bold text-gray-700">
                  <i className="fas fa-rocket text-teal-500 mt-1"></i>
                  <span>{text}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Partner Details */}
      <section className="py-32 px-6">
        <div className="max-w-4xl mx-auto text-center space-y-16">
          <h2 className="text-4xl font-black tracking-tight">Zero changes to your tech stack.</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10 text-left font-medium text-gray-500 leading-relaxed">
            <div className="space-y-4">
              <p>Clero doesn't touch your payment processor. You receive 100% of the funds from the organizer exactly as you do today.</p>
            </div>
            <div className="space-y-4">
              <p>Clero is built for high-AOV purchases—tickets, travel, and more. You can disable the integration at any time with a single toggle.</p>
            </div>
          </div>
          <button
            onClick={() => setIsContactModalOpen(true)}
            className="px-12 py-5 bg-teal-500 text-white rounded-[2rem] font-bold text-lg hover:bg-teal-400 transition-all shadow-xl shadow-teal-500/20"
          >
            Let's talk integration
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-20 border-t border-gray-100 px-6 text-center">
        <Logo size="sm" className="mx-auto" />
      </footer>

      {/* Contact Modal */}
      {isContactModalOpen && (
        <div className="fixed inset-0 z-[500] flex items-center justify-center p-4 bg-black/40 backdrop-blur-md animate-in fade-in duration-300">
          <div className="bg-white rounded-[2.5rem] w-full max-w-lg p-8 md:p-12 shadow-2xl relative overflow-hidden transition-all max-h-[90vh] overflow-y-auto">
            <button
              disabled={formState === 'submitting'}
              onClick={() => {
                if (formState === 'submitting') return;
                activeSubmissionRef.current = null;
                setIsContactModalOpen(false);
                setFormState('idle');
              }}
              className="absolute top-8 right-8 text-gray-300 hover:text-black transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <i className="fas fa-times text-xl"></i>
            </button>

            {formState === 'success' ? (
              <div className="text-center py-12 space-y-4 animate-in zoom-in duration-500">
                <div className="w-16 h-16 bg-teal-500 text-white rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <i className="fas fa-check text-2xl"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 tracking-tight">Request Received</h3>
                <p className="text-gray-500 font-medium text-sm leading-relaxed">
                  We've received your inquiry. A Clero partner manager will reach out to you within 24 hours.
                </p>
                <button
                  onClick={() => setIsContactModalOpen(false)}
                  className="mt-8 px-8 py-3 bg-gray-100 text-gray-900 rounded-xl font-bold text-sm hover:bg-gray-200 transition-colors"
                >
                  Close
                </button>
              </div>
            ) : (
              <div className="space-y-8">
                <div className="space-y-2">
                  <Logo size="sm" />
                  <h3 className="text-2xl font-bold text-gray-900 tracking-tight">Partner Inquiry</h3>
                  <p className="text-gray-500 font-medium text-sm leading-relaxed">
                    Start a conversation about integrating Clero.
                  </p>
                </div>

                <form onSubmit={handleContactSubmit} className="space-y-5">
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">Company & Name</label>
                    <input
                      required
                      disabled={formState === 'submitting'}
                      name="name"
                      type="text"
                      value={contactName}
                      onChange={(e) => updateContactName(e.target.value)}
                      placeholder="Alex @ Acme Stays"
                      className="w-full px-5 py-4 bg-gray-50 border border-transparent rounded-2xl text-sm font-medium focus:bg-white focus:border-black outline-none transition-all disabled:opacity-50"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">Work Email</label>
                    <input
                      required
                      disabled={formState === 'submitting'}
                      name="email"
                      type="email"
                      value={contactEmail}
                      onChange={(e) => updateContactEmail(e.target.value)}
                      placeholder="alex@acmestays.com"
                      className="w-full px-5 py-4 bg-gray-50 border border-transparent rounded-2xl text-sm font-medium focus:bg-white focus:border-black outline-none transition-all disabled:opacity-50"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">Direct Phone</label>
                    <PhoneInput
                      disabled={formState === 'submitting'}
                      value={phone}
                      onChange={(val) => updatePhone(val)}
                    />
                  </div>
                  <div className="pt-4">
                    <button
                      disabled={formState === 'submitting'}
                      type="submit"
                      className="w-full bg-teal-500 text-white py-5 rounded-2xl font-bold text-base hover:bg-teal-400 transition-all shadow-lg flex items-center justify-center space-x-3 disabled:opacity-50"
                    >
                      {formState === 'submitting' ? (
                        <>
                          <i className="fas fa-circle-notch fa-spin"></i>
                          <span>Connecting...</span>
                        </>
                      ) : (
                        <span>Request Pilot Deck</span>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PilotPage;
